import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, TextField, MenuItem, Button, CircularProgress, Card, CardContent,
  IconButton, Tooltip, FormControlLabel, Checkbox, Chip, Divider, 
} from '@mui/material';
import {
  Refresh as RefreshIcon, FileDownload as ExcelIcon, PictureAsPdf as PdfIcon,
  Print as PrintIcon, AccountBalance, CheckCircle, Warning, FilterList, AccountTree,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { financialReportsService, TrialBalanceFilter, TrialBalanceReportResult, TrialBalanceDto } from '../../services/financialReports.service';
import { fiscalPeriodsService } from '../../services/fiscalPeriods.service';
import { companyInfoService } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import HierarchicalTrialBalanceModal from './HierarchicalTrialBalanceModal';

// Local Types
interface FiscalPeriod {
  fiscalPeriodId: number;
  periodName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isClosed: boolean;
}

interface CompanyInfo {
  companyName?: string;
  companyNameAr?: string;
  logoBase64?: string | null;
  address?: string;
  phone?: string;
  email?: string;
}

const translations = {
  en: {
    title: 'Trial Balance',
    titleAr: 'ميزان المراجعة',
    filters: 'Filters',
    fromDate: 'From Date',
    toDate: 'To Date',
    fiscalPeriod: 'Fiscal Period',
    selectPeriod: 'Select Period',
    accountType: 'Account Type',
    allTypes: 'All Types',
    accountLevel: 'Account Level',
    allLevels: 'All Levels',
    includeZeroBalances: 'Include Zero Balances',
    onlyFinalAccounts: 'Final Accounts Only',
    showOpeningBalance: 'Show Opening Balance',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    accountCode: 'Account Code',
    accountName: 'Account Name',
    openingDebit: 'Opening Debit',
    openingCredit: 'Opening Credit',
    periodDebit: 'Period Debit',
    periodCredit: 'Period Credit',
    closingDebit: 'Closing Debit',
    closingCredit: 'Closing Credit',
    balance: 'Balance',
    total: 'Total',
    summary: 'Summary',
    totalAccounts: 'Total Accounts',
    accountsWithBalance: 'Accounts with Balance',
    isBalanced: 'Is Balanced',
    difference: 'Difference',
    balanced: 'Balanced',
    notBalanced: 'Not Balanced',
    noData: 'No data available',
    loading: 'Loading...',
    kwd: 'KWD',
    generatedAt: 'Generated at',
    hierarchicalView: 'Hierarchical View',
    asset: 'Asset',
    liability: 'Liability',
    equity: 'Equity',
    revenue: 'Revenue',
    expense: 'Expense',
    level: 'Level',
    debit: 'Debit',
    credit: 'Credit',
  },
  ar: {
    title: 'ميزان المراجعة',
    titleAr: 'ميزان المراجعة',
    filters: 'الفلاتر',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    fiscalPeriod: 'السنة المالية',
    selectPeriod: 'اختر السنة',
    accountType: 'نوع الحساب',
    allTypes: 'جميع الأنواع',
    accountLevel: 'مستوى الحساب',
    allLevels: 'جميع المستويات',
    includeZeroBalances: 'عرض الأرصدة الصفرية',
    onlyFinalAccounts: 'الحسابات النهائية فقط',
    showOpeningBalance: 'عرض الرصيد الافتتاحي',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    accountCode: 'رقم الحساب',
    accountName: 'اسم الحساب',
    openingDebit: 'مدين افتتاحي',
    openingCredit: 'دائن افتتاحي',
    periodDebit: 'مدين الفترة',
    periodCredit: 'دائن الفترة',
    closingDebit: 'مدين ختامي',
    closingCredit: 'دائن ختامي',
    balance: 'الرصيد',
    total: 'الإجمالي',
    summary: 'الملخص',
    totalAccounts: 'إجمالي الحسابات',
    accountsWithBalance: 'حسابات لها رصيد',
    isBalanced: 'متوازن',
    difference: 'الفرق',
    balanced: 'متوازن',
    notBalanced: 'غير متوازن',
    noData: 'لا توجد بيانات',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    generatedAt: 'تاريخ التقرير',
    hierarchicalView: 'عرض هرمي',
    asset: 'أصول',
    liability: 'التزامات',
    equity: 'حقوق ملكية',
    revenue: 'إيرادات',
    expense: 'مصروفات',
    level: 'المستوى',
    debit: 'مدين',
    credit: 'دائن',
  },
};

const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

const TrialBalancePage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  // State
  // State
  const [hierarchicalModalOpen, setHierarchicalModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<TrialBalanceReportResult | null>(null);
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Filters
  const [filter, setFilter] = useState<TrialBalanceFilter>({
    includeZeroBalances: false,
    onlyFinalAccounts: true,
    showOpeningBalance: true,
  });

  // Load initial data
  useEffect(() => {
    loadFiscalPeriods();
    loadCompanyInfo();
  }, []);

  const loadFiscalPeriods = async () => {
    try {
      const periods = await fiscalPeriodsService.getAll();
      setFiscalPeriods(periods);
      const currentPeriod = periods.find(p => p.isCurrent);
      if (currentPeriod) {
        setFilter(prev => ({ ...prev, fiscalPeriodId: currentPeriod.fiscalPeriodId }));
      }
    } catch (error) {
      console.error('Error loading fiscal periods:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Error loading company info:', error);
    }
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const result = await financialReportsService.getTrialBalance(filter);
      setReportData(result);
    } catch (error: any) {
      console.error('Error loading trial balance:', error);
      toast.error(error.response?.data?.message || 'Error loading report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (filter.fiscalPeriodId || filter.fromDate || filter.toDate) {
      loadReport();
    }
  }, []);

  const handleFilterChange = (field: keyof TrialBalanceFilter, value: any) => {
    setFilter(prev => ({ ...prev, [field]: value }));
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  };

  const getAccountTypeName = (type: string): string => {
    const typeMap: Record<string, { en: string; ar: string }> = {
      'Asset': { en: 'Asset', ar: 'أصول' },
      'Liability': { en: 'Liability', ar: 'التزامات' },
      'Equity': { en: 'Equity', ar: 'حقوق ملكية' },
      'Revenue': { en: 'Revenue', ar: 'إيرادات' },
      'Expense': { en: 'Expense', ar: 'مصروفات' },
    };
    return typeMap[type]?.[language] || type;
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (!reportData?.data.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t.title);

    // Add logo if exists
    if (companyInfo?.logoBase64) {
      try {
        const imageId = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
        worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 80 } });
      } catch (e) { console.error('Error adding logo:', e); }
    }

    // Company Header
    worksheet.mergeCells('B1:I1');
    worksheet.getCell('B1').value = companyInfo?.companyName || 'Company Name';
    worksheet.getCell('B1').font = { size: 16, bold: true };
    worksheet.getCell('B1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B2:I2');
    worksheet.getCell('B2').value = t.title;
    worksheet.getCell('B2').font = { size: 14, bold: true };
    worksheet.getCell('B2').alignment = { horizontal: 'center' };

    // Period info
    worksheet.mergeCells('B3:I3');
    const periodText = reportData.fiscalPeriodName || 
      (reportData.fromDate && reportData.toDate ? `${reportData.fromDate} - ${reportData.toDate}` : '');
    worksheet.getCell('B3').value = periodText;
    worksheet.getCell('B3').alignment = { horizontal: 'center' };

    // Headers
    const headers = [
      t.accountCode, t.accountName, t.openingDebit, t.openingCredit,
      t.periodDebit, t.periodCredit, t.closingDebit, t.closingCredit, t.balance
    ];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1976D2' } };
      cell.alignment = { horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Data rows
    reportData.data.forEach(row => {
      const dataRow = worksheet.addRow([
        row.accountCode,
        isRTL ? row.accountNameAr : row.accountName,
        row.openingDebit,
        row.openingCredit,
        row.periodDebit,
        row.periodCredit,
        row.closingDebit,
        row.closingCredit,
        row.balance,
      ]);
      dataRow.eachCell((cell, colNumber) => {
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        if (colNumber >= 3) {
          cell.numFmt = '#,##0.000';
          cell.alignment = { horizontal: 'right' };
        }
      });
    });

    // Total row
    const summary = reportData.summary;
    const totalRow = worksheet.addRow([
      t.total, '',
      summary.totalOpeningDebit, summary.totalOpeningCredit,
      summary.totalPeriodDebit, summary.totalPeriodCredit,
      summary.totalClosingDebit, summary.totalClosingCredit,
      summary.totalClosingDebit - summary.totalClosingCredit,
    ]);
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E3F2FD' } };
      cell.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'thin' }, right: { style: 'thin' } };
      if (colNumber >= 3) cell.numFmt = '#,##0.000';
    });

    // Column widths
    worksheet.columns = [
      { width: 15 }, { width: 35 }, { width: 15 }, { width: 15 },
      { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }
    ];

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TrialBalance_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(isRTL ? 'تم تصدير Excel بنجاح' : 'Excel exported successfully');
  };

  // Export to PDF
  const exportToPDF = async () => {
    if (!reportData?.data.length) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Logo
    if (companyInfo?.logoBase64) {
      try {
        doc.addImage(companyInfo.logoBase64, 'PNG', 10, 5, 20, 20);
      } catch (e) { console.error('Error adding logo:', e); }
    }

    // Header
    doc.setFontSize(16);
    doc.text(companyInfo?.companyName || 'Company Name', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(14);
    doc.text(t.title, pageWidth / 2, 22, { align: 'center' });
    doc.setFontSize(10);
    const periodText = reportData.fiscalPeriodName || 
      (reportData.fromDate && reportData.toDate ? `${reportData.fromDate} - ${reportData.toDate}` : '');
    doc.text(periodText, pageWidth / 2, 28, { align: 'center' });

    // Table
    const headers = [[
      t.accountCode, t.accountName, t.openingDebit, t.openingCredit,
      t.periodDebit, t.periodCredit, t.closingDebit, t.closingCredit, t.balance
    ]];

    const data = reportData.data.map(row => [
      row.accountCode,
      isRTL ? row.accountNameAr : row.accountName,
      formatNumber(row.openingDebit),
      formatNumber(row.openingCredit),
      formatNumber(row.periodDebit),
      formatNumber(row.periodCredit),
      formatNumber(row.closingDebit),
      formatNumber(row.closingCredit),
      formatNumber(row.balance),
    ]);

    // Add totals
    const summary = reportData.summary;
    data.push([
      t.total, '',
      formatNumber(summary.totalOpeningDebit),
      formatNumber(summary.totalOpeningCredit),
      formatNumber(summary.totalPeriodDebit),
      formatNumber(summary.totalPeriodCredit),
      formatNumber(summary.totalClosingDebit),
      formatNumber(summary.totalClosingCredit),
      formatNumber(summary.totalClosingDebit - summary.totalClosingCredit),
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [227, 242, 253], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 50 },
        2: { halign: 'right', cellWidth: 25 },
        3: { halign: 'right', cellWidth: 25 },
        4: { halign: 'right', cellWidth: 25 },
        5: { halign: 'right', cellWidth: 25 },
        6: { halign: 'right', cellWidth: 25 },
        7: { halign: 'right', cellWidth: 25 },
        8: { halign: 'right', cellWidth: 25 },
      },
      didParseCell: (data) => {
        if (data.row.index === reportData.data.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [227, 242, 253];
        }
      },
    });

    doc.save(`TrialBalance_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(isRTL ? 'تم تصدير PDF بنجاح' : 'PDF exported successfully');
  };

  // Print
  const handlePrint = () => {
    if (!reportData?.data.length) return;

    // Respect includeZeroBalances filter: if false, exclude accounts with all-zero closing balances
    const printData = filter.includeZeroBalances
      ? reportData.data
      : reportData.data.filter(row => row.closingDebit !== 0 || row.closingCredit !== 0);

    const openingCols = filter.showOpeningBalance
      ? `<th>${t.openingDebit}</th><th>${t.openingCredit}</th>`
      : '';

    const openingTotals = filter.showOpeningBalance
      ? `<td class="number">${formatNumber(reportData.summary.totalOpeningDebit)}</td>
         <td class="number">${formatNumber(reportData.summary.totalOpeningCredit)}</td>`
      : '';

    const printContent = `
      <!DOCTYPE html>
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${t.title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; }
          .header { text-align: center; margin-bottom: 20px; }
          .header img { max-height: 60px; margin-bottom: 10px; display: block; margin-left: auto; margin-right: auto; }
          .header h1 { margin: 5px 0; font-size: 18px; }
          .header h2 { margin: 5px 0; font-size: 16px; color: #1976d2; }
          .header p { margin: 5px 0; color: #666; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; }
          th, td { border: 1px solid #ddd; padding: 5px 6px; text-align: ${isRTL ? 'right' : 'left'}; }
          th { background: #1976d2; color: white; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .number { text-align: right; font-family: monospace; }
          .total-row { background: #e3f2fd; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .summary { margin-top: 20px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; }
          .summary-card { padding: 10px 20px; border: 1px solid #ddd; border-radius: 5px; text-align: center; }
          .balanced { color: green; }
          .not-balanced { color: red; }
          @media print { body { margin: 10px; } @page { margin: 12mm; size: A4 landscape; } }
        </style>
      </head>
      <body>
        <div class="header">
          ${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}" alt="Logo"/>` : ''}
          <h1>${isRTL ? (companyInfo?.companyNameAr || companyInfo?.companyName || '') : (companyInfo?.companyName || '')}</h1>
          <h2>${t.title}</h2>
          <p>${reportData.fiscalPeriodName || (reportData.fromDate && reportData.toDate ? `${reportData.fromDate} - ${reportData.toDate}` : '')}</p>
          ${!filter.includeZeroBalances ? `<p style="color:#888;font-size:10px;">(${isRTL ? 'مستثنى الأرصدة الصفرية' : 'Zero balances excluded'})</p>` : ''}
        </div>
        <table>
          <thead>
            <tr>
              <th>${t.accountCode}</th>
              <th>${t.accountName}</th>
              ${openingCols}
              <th>${t.periodDebit}</th>
              <th>${t.periodCredit}</th>
              <th>${t.closingDebit}</th>
              <th>${t.closingCredit}</th>
              <th>${t.balance}</th>
            </tr>
          </thead>
          <tbody>
            ${printData.map(row => `
              <tr>
                <td>${row.accountCode}</td>
                <td>${isRTL ? row.accountNameAr : row.accountName}</td>
                ${filter.showOpeningBalance
                  ? `<td class="number">${formatNumber(row.openingDebit)}</td>
                     <td class="number">${formatNumber(row.openingCredit)}</td>`
                  : ''}
                <td class="number">${formatNumber(row.periodDebit)}</td>
                <td class="number">${formatNumber(row.periodCredit)}</td>
                <td class="number">${formatNumber(row.closingDebit)}</td>
                <td class="number">${formatNumber(row.closingCredit)}</td>
                <td class="number">${formatNumber(row.balance)}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="2">${t.total}</td>
              ${openingTotals}
              <td class="number">${formatNumber(reportData.summary.totalPeriodDebit)}</td>
              <td class="number">${formatNumber(reportData.summary.totalPeriodCredit)}</td>
              <td class="number">${formatNumber(reportData.summary.totalClosingDebit)}</td>
              <td class="number">${formatNumber(reportData.summary.totalClosingCredit)}</td>
              <td class="number">${formatNumber(reportData.summary.totalClosingDebit - reportData.summary.totalClosingCredit)}</td>
            </tr>
          </tbody>
        </table>
        <div class="summary">
          <div class="summary-card">
            <strong>${t.totalAccounts}</strong><br/>${printData.length}
          </div>
          <div class="summary-card">
            <strong>${t.isBalanced}</strong><br/>
            <span class="${reportData.summary.isBalanced ? 'balanced' : 'not-balanced'}">
              ${reportData.summary.isBalanced ? t.balanced : t.notBalanced}
            </span>
          </div>
          <div class="summary-card">
            <strong>${t.difference}</strong><br/>${formatNumber(reportData.summary.difference)} ${t.kwd}
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 500);
    }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalance sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadReport} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton onClick={exportToExcel} color="success" disabled={!reportData?.data.length}><ExcelIcon /></IconButton>
          </Tooltip>
          <Tooltip title={t.exportPdf}>
            <IconButton onClick={exportToPDF} color="error" disabled={!reportData?.data.length}><PdfIcon /></IconButton>
          </Tooltip>
          <Tooltip title={t.print}>
            <IconButton onClick={handlePrint} color="info" disabled={!reportData?.data.length}><PrintIcon /></IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Tooltip title={t.hierarchicalView}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AccountTree />}
              onClick={() => setHierarchicalModalOpen(true)}
              size="small"
            >
              {t.hierarchicalView}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterList color="action" />
          <Typography variant="subtitle1" fontWeight="medium">{t.filters}</Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <Box sx={{ flex: "1 1 150px" }}>
            <TextField
              select fullWidth size="small" label={t.fiscalPeriod}
              value={filter.fiscalPeriodId || ''}
              onChange={(e) => handleFilterChange('fiscalPeriodId', e.target.value ? Number(e.target.value) : undefined)}
            >
              <MenuItem value="">{t.selectPeriod}</MenuItem>
              {fiscalPeriods.map(p => (
                <MenuItem key={p.fiscalPeriodId} value={p.fiscalPeriodId}>
                  {p.periodName} {p.isCurrent && '(Current)'}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: "1 1 150px" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.fromDate}
                value={filter.fromDate ? new Date(filter.fromDate) : null}
                onChange={(date) => {
                  const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  handleFilterChange('fromDate', newValue);
                  if (filter.toDate && newValue && filter.toDate < newValue) handleFilterChange('toDate', '');
                }}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                openTo="day"
                minDate={minDate}
                maxDate={filter.toDate ? new Date(filter.toDate) : maxDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: "1 1 150px" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.toDate}
                value={filter.toDate ? new Date(filter.toDate) : null}
                onChange={(date) => {
                  const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  handleFilterChange('toDate', newValue);
                  if (filter.fromDate && newValue && filter.fromDate > newValue) handleFilterChange('fromDate', '');
                }}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                openTo="day"
                minDate={filter.fromDate ? new Date(filter.fromDate) : minDate}
                maxDate={maxDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: "1 1 150px" }}>
            <TextField
              select fullWidth size="small" label={t.accountType}
              value={filter.accountType || ''}
              onChange={(e) => handleFilterChange('accountType', e.target.value || undefined)}
            >
              <MenuItem value="">{t.allTypes}</MenuItem>
              {accountTypes.map(type => (
                <MenuItem key={type} value={type}>{getAccountTypeName(type)}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: "1 1 150px" }}>
            <TextField
              select fullWidth size="small" label={t.accountLevel}
              value={filter.accountLevel || ''}
              onChange={(e) => handleFilterChange('accountLevel', e.target.value ? Number(e.target.value) : undefined)}
            >
              <MenuItem value="">{t.allLevels}</MenuItem>
              {[1, 2, 3, 4, 5].map(level => (
                <MenuItem key={level} value={level}>{t.level} {level}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: "1 1 150px" }}>
            <Button variant="contained" fullWidth onClick={loadReport} startIcon={<RefreshIcon />}>
              {t.refresh}
            </Button>
          </Box>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Checkbox checked={filter.includeZeroBalances} onChange={(e) => handleFilterChange('includeZeroBalances', e.target.checked)} />}
                label={t.includeZeroBalances}
              />
              <FormControlLabel
                control={<Checkbox checked={filter.onlyFinalAccounts} onChange={(e) => handleFilterChange('onlyFinalAccounts', e.target.checked)} />}
                label={t.onlyFinalAccounts}
              />
              <FormControlLabel
                control={<Checkbox checked={filter.showOpeningBalance} onChange={(e) => handleFilterChange('showOpeningBalance', e.target.checked)} />}
                label={t.showOpeningBalance}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Summary Cards */}
      {reportData?.summary && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <Box sx={{ flex: "1 1 200px" }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>{t.totalAccounts}</Typography>
                <Typography variant="h4" color="primary">{reportData.summary.totalAccounts}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: "1 1 200px" }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>{t.accountsWithBalance}</Typography>
                <Typography variant="h4" color="secondary">{reportData.summary.accountsWithBalance}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: "1 1 200px" }}>
            <Card sx={{ bgcolor: reportData.summary.isBalanced ? 'success.light' : 'error.light' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>{t.isBalanced}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {reportData.summary.isBalanced ? 
                    <CheckCircle color="success" /> : <Warning color="error" />}
                  <Typography variant="h6">
                    {reportData.summary.isBalanced ? t.balanced : t.notBalanced}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: "1 1 200px" }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>{t.difference}</Typography>
                <Typography variant="h5" color={reportData.summary.difference === 0 ? 'success.main' : 'error.main'}>
                  {formatNumber(reportData.summary.difference)} {t.kwd}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : !reportData?.data.length ? (
          <Box sx={{ textAlign: 'center', p: 5 }}>
            <Typography color="textSecondary">{t.noData}</Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.accountCode}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.accountName}</TableCell>
                {filter.showOpeningBalance && (
                  <>
                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.openingDebit}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.openingCredit}</TableCell>
                  </>
                )}
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.periodDebit}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.periodCredit}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.closingDebit}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.closingCredit}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t.balance}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.data.map((row, index) => (
                <TableRow key={row.accountId} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                  <TableCell>
                    <Chip label={row.accountCode} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{isRTL ? row.accountNameAr : row.accountName}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {getAccountTypeName(row.accountType)} • L{row.accountLevel}
                      </Typography>
                    </Box>
                  </TableCell>
                  {filter.showOpeningBalance && (
                    <>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatNumber(row.openingDebit)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatNumber(row.openingCredit)}</TableCell>
                    </>
                  )}
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatNumber(row.periodDebit)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatNumber(row.periodCredit)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(row.closingDebit)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(row.closingCredit)}</TableCell>
                  <TableCell align="right" sx={{ 
                    fontFamily: 'monospace', fontWeight: 'bold',
                    color: row.balance > 0 ? 'success.main' : row.balance < 0 ? 'error.main' : 'text.primary'
                  }}>
                    {formatNumber(row.balance)}
                  </TableCell>
                </TableRow>
              ))}
              {/* Total Row */}
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.total}</TableCell>
                {filter.showOpeningBalance && (
                  <>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {formatNumber(reportData.summary.totalOpeningDebit)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {formatNumber(reportData.summary.totalOpeningCredit)}
                    </TableCell>
                  </>
                )}
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {formatNumber(reportData.summary.totalPeriodDebit)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {formatNumber(reportData.summary.totalPeriodCredit)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {formatNumber(reportData.summary.totalClosingDebit)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {formatNumber(reportData.summary.totalClosingCredit)}
                </TableCell>
                <TableCell align="right" sx={{ 
                  fontFamily: 'monospace', fontWeight: 'bold',
                  color: reportData.summary.isBalanced ? 'success.main' : 'error.main'
                }}>
                  {formatNumber(reportData.summary.totalClosingDebit - reportData.summary.totalClosingCredit)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Footer */}
      {reportData && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', color: 'text.secondary' }}>
          <Typography variant="caption">
            {t.generatedAt}: {new Date(reportData.generatedAt).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}
          </Typography>
          <Typography variant="caption">
            {reportData.fiscalPeriodName}
          </Typography>
        </Box>
      )}

      {/* Hierarchical Trial Balance Modal */}
      <HierarchicalTrialBalanceModal
        open={hierarchicalModalOpen}
        onClose={() => setHierarchicalModalOpen(false)}
      />
    </Box>
  );
};

export default TrialBalancePage;